import { BigNumber } from '@ethersproject/bignumber'
import { splitSignature } from '@ethersproject/bytes'
import { Contract } from '@ethersproject/contracts'
import { formatUnits } from '@ethersproject/units'
import { useCallback, useMemo, useState } from 'react'

import { EIP712_DOMAIN_TYPE, PERMIT_TYPE } from '../constants/abis/conveyor-v2'
import { ROUTER_ADDRESS } from '../data/sunset/types'
import { useActiveWeb3React } from './index'
import { useConveyorV2RouterContract, usePairContract } from './useContract'

// ERC20 Transfer event topic
const TRANSFER_TOPIC = '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef'

// Minimal ERC20 ABI for token info
const ERC20_ABI = [
  'function symbol() view returns (string)',
  'function decimals() view returns (uint8)'
]

export interface WithdrawnToken {
  address: string
  symbol: string
  amount: string
  decimals: number
}

export interface WithdrawState {
  status: 'idle' | 'signing' | 'pending' | 'success' | 'error'
  txHash?: string
  error?: string
  withdrawnTokens?: WithdrawnToken[]
}

export function useSunsetWithdraw(pairAddress: string | undefined) {
  const { account, chainId, library } = useActiveWeb3React()
  const routerContract = useConveyorV2RouterContract()
  const pairContract = usePairContract(pairAddress)

  const [state, setState] = useState<WithdrawState>({ status: 'idle' })

  const withdraw = useCallback(async () => {
    if (!account || !chainId || !library || !pairContract || !routerContract || !pairAddress) {
      setState({ status: 'error', error: 'Missing dependencies' })
      return
    }

    try {
      setState({ status: 'signing' })

      // Get user's LP balance
      const lpBalance: BigNumber = await pairContract.balanceOf(account)
      if (lpBalance.isZero()) {
        setState({ status: 'error', error: 'No LP tokens to withdraw' })
        return
      }

      // Get token addresses from pair
      const token0: string = await pairContract.token0()
      const token1: string = await pairContract.token1()

      // Get nonce for permit
      const nonce: BigNumber = await pairContract.nonces(account)

      // Set deadline to 20 minutes from now
      const deadline = BigNumber.from(Math.floor(Date.now() / 1000) + 20 * 60)

      // Create EIP-712 permit message
      const domain = {
        name: 'Conveyor V2',
        version: '1',
        chainId: BigNumber.from(chainId).toHexString(),
        verifyingContract: pairAddress
      }

      const message = {
        owner: account,
        spender: ROUTER_ADDRESS,
        value: lpBalance.toHexString(),
        nonce: nonce.toHexString(),
        deadline: deadline.toHexString()
      }

      const EIP712Msg = {
        types: {
          EIP712Domain: EIP712_DOMAIN_TYPE,
          Permit: PERMIT_TYPE
        },
        domain,
        primaryType: 'Permit',
        message
      }

      // Sign the permit
      const data = JSON.stringify(EIP712Msg)
      const signature = await library.send('eth_signTypedData_v4', [account, data])
      const { r, s, v } = splitSignature(signature)

      // Build the remove liquidity params
      // Set amountMin to 0 for sunset (accept any slippage to ensure withdrawal works)
      const removeParams = {
        tokenA: token0,
        tokenB: token1,
        liquidity: lpBalance,
        amountAMin: BigNumber.from(0),
        amountBMin: BigNumber.from(0),
        user: account,
        deadline: deadline
      }

      const sigParams = {
        v,
        r,
        s
      }

      // Call removeLiquidityWithPermit directly on router
      const tx = await routerContract.removeLiquidityWithPermit(removeParams, sigParams, {
        gasLimit: BigNumber.from(300000)
      })

      // Transaction submitted, now waiting for confirmation
      setState({ status: 'pending' })

      const receipt = await tx.wait()

      // Parse Transfer events to get withdrawn amounts
      const withdrawnTokens: WithdrawnToken[] = []
      const tokenAddresses = [token0, token1]

      for (const log of receipt.logs) {
        // Check if this is a Transfer event to the user
        if (
          log.topics[0] === TRANSFER_TOPIC &&
          log.topics.length >= 3 &&
          log.topics[2].toLowerCase().includes(account.toLowerCase().slice(2))
        ) {
          const tokenAddress = log.address
          // Only process if it's one of the pair tokens
          if (tokenAddresses.some(t => t.toLowerCase() === tokenAddress.toLowerCase())) {
            try {
              const tokenContract = new Contract(tokenAddress, ERC20_ABI, library)
              const [symbol, decimals] = await Promise.all([
                tokenContract.symbol(),
                tokenContract.decimals()
              ])
              const amount = BigNumber.from(log.data)
              withdrawnTokens.push({
                address: tokenAddress,
                symbol,
                amount: formatUnits(amount, decimals),
                decimals
              })
            } catch (err) {
              console.warn('Failed to parse token info:', err)
            }
          }
        }
      }

      setState({ status: 'success', txHash: receipt.transactionHash, withdrawnTokens })
    } catch (err) {
      const error = err as { code?: number | string; message?: string }
      console.error('Withdrawal error:', error)
      // Handle user rejection
      if (error?.code === 4001 || error?.code === 'ACTION_REJECTED') {
        setState({ status: 'idle' })
      } else {
        setState({ status: 'error', error: error?.message || 'Withdrawal failed' })
      }
    }
  }, [account, chainId, library, pairContract, routerContract, pairAddress])

  const reset = useCallback(() => {
    setState({ status: 'idle' })
  }, [])

  return useMemo(
    () => ({
      withdraw,
      reset,
      ...state
    }),
    [withdraw, reset, state]
  )
}

// Hook to get on-chain LP balance for a pair
export function useLPBalance(pairAddress: string | undefined) {
  const { account } = useActiveWeb3React()
  const pairContract = usePairContract(pairAddress)
  const [balance, setBalance] = useState<BigNumber | null>(null)
  const [loading, setLoading] = useState(false)

  const refresh = useCallback(async () => {
    if (!account || !pairContract) {
      setBalance(null)
      return
    }

    setLoading(true)
    try {
      const bal = await pairContract.balanceOf(account)
      setBalance(bal)
    } catch (error) {
      console.error('Error fetching LP balance:', error)
      setBalance(null)
    } finally {
      setLoading(false)
    }
  }, [account, pairContract])

  return { balance, loading, refresh }
}
